import { isProduction } from '../env/EnvUtils';

interface LogOptions {
  level: 'debug' | 'info' | 'warn' | 'error'; // Level is always specified internally
}

class Logger {
  private readonly context: string;
  private readonly isProduction: boolean;

  constructor(context: string) {
    this.context = context;
    this.isProduction = isProduction();
  }

  private formatMessage(message: string, options?: LogOptions): string {
    const timestamp = new Date().toISOString();
    const level = options?.level?.toUpperCase() || 'INFO';
    const finalContext = this.context;
    return `${timestamp} ${level} [${finalContext}] ${message}`;
  }

  private shouldLog(options: LogOptions): boolean {
    if (this.isProduction) {
      return options.level === 'error';
    }
    return true;
  }

  debug(message: string, data?: unknown): void {
    if (!this.shouldLog({ level: 'debug' })) return;
    const formattedMessage = this.formatMessage(message, { level: 'debug' });
    console.debug(`%c${formattedMessage}`, 'color: #666666', data || '');
  }

  info(message: string, data?: unknown): void {
    if (!this.shouldLog({ level: 'info' })) return;
    const formattedMessage = this.formatMessage(message, { level: 'info' });
    console.info(formattedMessage, data || '');
  }

  warn(message: string, data?: unknown): void {
    if (!this.shouldLog({ level: 'warn' })) return;
    const formattedMessage = this.formatMessage(message, { level: 'warn' });
    console.warn(`%c${formattedMessage}`, 'color: #FF9800', data || '');
  }

  error(message: string, errorValue?: unknown): void {
    if (!this.shouldLog({ level: 'error' })) return;
    const formattedMessage = this.formatMessage(message, { level: 'error' });
    console.error(`%c${formattedMessage}`, 'color: #F44336', errorValue || '');
  }

  logFeatureFlag(flagName: string, isEnabled: boolean, source: string): void {
    if (!this.shouldLog({ level: 'debug' })) return;
    this.debug(`Feature flag "${flagName}" is ${isEnabled ? 'enabled' : 'disabled'}`, { source });
  }

  logApiCall(endpoint: string, method: string, status: number, response?: unknown): void {
    if (!this.shouldLog({ level: 'debug' })) return;
    this.debug(`API ${method} ${endpoint}`, {
      status,
      response: response || 'No response data',
    });
  }

  logStateChange(component: string, previousState: unknown, newState: unknown): void {
    if (!this.shouldLog({ level: 'debug' })) return;
    this.debug(`State change in ${component}`, {
      previous: previousState,
      new: newState,
    });
  }
}

export const createLogger = (context: string): Logger => new Logger(context);
